import { DeliveryOptionWithTotals } from 'types/delivery.types';
import { DeliveryMethodType } from 'types/delivery-method.types';

import { pickPreselectedShippingMethod } from './pickPreselectedShippingMethod';
import { rankDeliveryMethods } from './rankDeliveryMethods';
import { moveToFront, sortByRank } from './sorting';

type Prefill = Parameters<typeof pickPreselectedShippingMethod>[0] & Parameters<typeof rankDeliveryMethods>[1];

export type CampaignDeliveryLine =
  | { type: 'free'; option: DeliveryOptionWithTotals; reach: 'some' | 'first-order' | 'everyone' }
  | { type: 'discounted'; option: DeliveryOptionWithTotals }
  | { type: 'regular'; option: DeliveryOptionWithTotals };

const shippingFee = (option: DeliveryOptionWithTotals) => parseFloat(option.pricing.shipping);

const isPaid = (option: DeliveryOptionWithTotals) => parseFloat(option.pricing.preCampaignShipping ?? option.pricing.shipping) > 0;

const isCampaignLowered = (option: DeliveryOptionWithTotals) => !!option.pricing.preCampaignShipping;

/**
 * The providers the product page can name, in the order checkout lists them: the one checkout
 * preselects first, then its ranking (DEV-2422) or the market order. Checkout also ranks the
 * providers it fills from the buyer's address, which the product page doesn't load, so those keep
 * the market order here.
 */
export const orderLikeCheckout = (
  shippingOptionsWithTotals: Record<string, DeliveryOptionWithTotals> | null | undefined,
  prefill: Prefill,
  isRankingOn: boolean,
): DeliveryOptionWithTotals[] => {
  const checkoutOptions = Object.values(shippingOptionsWithTotals ?? {}).filter(({ name }) => name !== DeliveryMethodType.BUNDLING);
  const ranking = isRankingOn ? rankDeliveryMethods(checkoutOptions, prefill, [], []) : undefined;
  const preselected =
    checkoutOptions.length === 1
      ? checkoutOptions[0].name
      : (pickPreselectedShippingMethod(prefill, (method) => checkoutOptions.some(({ name }) => name === method)) ?? ranking?.firstReady);

  return moveToFront([...checkoutOptions].sort(sortByRank(ranking?.order)), ({ name }) => name === preselected).filter(
    (option) => option.commercialShipping && !option.comingSoon && !option.disabledForBuyer,
  );
};

/**
 * The same providers cheapest first, ties in market order: what the product page named before it
 * followed checkout, and still names while `pdp_delivery_line_checkout_order` is off.
 */
export const orderByPrice = (
  shippingOptionsWithTotals: Record<string, DeliveryOptionWithTotals> | null | undefined,
): DeliveryOptionWithTotals[] =>
  [...orderLikeCheckout(shippingOptionsWithTotals, undefined, false)].sort((a, b) => shippingFee(a) - shippingFee(b));

/**
 * The campaign line for options already in the order the line uses (DEV-2352). A campaign that makes any paid
 * provider free wins over one that only discounts. Otherwise the line names the cheapest provider of
 * all, so "Delivery from" stays true: discounted when a campaign lowered its fee, regular when an
 * undiscounted provider is cheaper. Undefined when no campaign lowered a paid fee.
 */
export const getCampaignDeliveryLine = (orderedOptions: DeliveryOptionWithTotals[]): CampaignDeliveryLine | undefined => {
  const paidOptions = orderedOptions.filter(isPaid);
  const freeOptions = paidOptions.filter((option) => isCampaignLowered(option) && shippingFee(option) === 0);

  if (freeOptions.length) {
    if (freeOptions.length < paidOptions.length) {
      return { type: 'free', option: freeOptions[0], reach: 'some' };
    }

    return {
      type: 'free',
      option: freeOptions[0],
      reach: freeOptions.every((option) => option.hasNewBuyersOnlyPromo) ? 'first-order' : 'everyone',
    };
  }

  if (!paidOptions.some(isCampaignLowered)) {
    return undefined;
  }

  const cheapestOption = orderedOptions.reduce((cheapest, option) => (shippingFee(option) < shippingFee(cheapest) ? option : cheapest));

  return { type: isCampaignLowered(cheapestOption) ? 'discounted' : 'regular', option: cheapestOption };
};
